import React, { useState, useEffect, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { Checkbox } from "primereact/checkbox";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { useNavigate } from "react-router-dom";
import StatCard from "../Dashboard/StatCard";
import { getSchools, getBillingPortalUrl, updateSchool } from "../../api/setupApi";
import { useAuth } from "../../common/contexts/AuthContext";

const PlatformDashboard = () => {
  const navigate = useNavigate();
  const { enterSchool } = useAuth();
  const toast = useRef(null);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [resetPassword, setResetPassword] = useState(false);

  useEffect(() => {
    fetchSchools();
  }, []);

  const fetchSchools = async () => {
    try {
      const result = await getSchools();
      if (result.success) setSchools(result.data);
    } catch (err) {
      console.error("Error fetching schools:", err);
    } finally {
      setLoading(false);
    }
  };

  const counts = {
    total: schools.length,
    active: schools.filter((s) => s.setupStatus === "active").length,
    provisioning: schools.filter((s) => s.setupStatus === "provisioning").length,
    failed: schools.filter((s) => s.setupStatus === "failed").length,
  };

  const statusTemplate = (rowData) => {
    const map = {
      active: "success",
      provisioning: "info",
      failed: "danger",
      pending: "warning",
    };
    return (
      <Tag
        value={rowData.setupStatus}
        severity={map[rowData.setupStatus] || "warning"}
      />
    );
  };

  const planTemplate = (rowData) => {
    if (!rowData.plan) return <span className="text-400">—</span>;
    const colors = { free: "success", basic: "info", pro: "warning", enterprise: "danger" };
    return <Tag value={rowData.plan.name} severity={colors[rowData.plan.tier] || "info"} />;
  };

  const subscriptionTemplate = (rowData) => {
    if (!rowData.subscriptions || rowData.subscriptions.length === 0) {
      return <span className="text-400">—</span>;
    }
    const sub = rowData.subscriptions[0];
    const colors = { active: "success", trialing: "info", past_due: "warning", canceled: "danger", pending: "warning" };
    return <Tag value={sub.status} severity={colors[sub.status] || "warning"} />;
  };

  const dateTemplate = (rowData) => {
    return new Date(rowData.createdAt).toLocaleDateString();
  };

  const handleEnterSchool = (school) => {
    enterSchool(school);
    navigate('/index/dashboard');
  };

  const handleBillingPortal = async (school) => {
    try {
      const result = await getBillingPortalUrl(school.id);
      if (result.success && result.url) {
        window.open(result.url, '_blank');
      }
    } catch (err) {
      console.error("Error opening billing portal:", err);
    }
  };

  const openEditDialog = (school) => {
    const sd = school.setupData || {};
    setEditData({
      id: school.id,
      schoolName: school.schoolName || "",
      adminEmail: school.adminEmail || "",
      email: sd.email || "",
      phone: sd.phone || "",
      address: sd.address || "",
      country: sd.country || "",
      adminName: sd.adminName || "",
      customDomain: school.customDomain || "",
    });
    setResetPassword(false);
    setEditDialog(true);
  };

  const handleEditSave = async () => {
    if (!editData.schoolName || !editData.adminEmail) {
      toast.current?.show({ severity: "warn", summary: "Validation", detail: "School name and admin email are required" });
      return;
    }
    if (resetPassword && (!editData.newPassword || editData.newPassword.length < 6)) {
      toast.current?.show({ severity: "warn", summary: "Validation", detail: "Password must be at least 6 characters" });
      return;
    }
    setSaving(true);
    try {
      const { id, newPassword, ...data } = editData;
      if (resetPassword && newPassword) data.newPassword = newPassword;
      const result = await updateSchool(id, data);
      if (result.success) {
        toast.current?.show({ severity: "success", summary: "Success", detail: "School updated successfully" });
        setEditDialog(false);
        fetchSchools();
      }
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to update school";
      toast.current?.show({ severity: "error", summary: "Error", detail: msg });
    } finally {
      setSaving(false);
    }
  };

  const actionTemplate = (rowData) => (
    <div className="flex gap-1">
      <Button
        icon="pi pi-pencil"
        className="p-button-sm p-button-outlined p-button-info"
        onClick={() => openEditDialog(rowData)}
        tooltip="Edit school"
        tooltipOptions={{ position: 'left' }}
      />
      <Button
        icon="pi pi-sign-in"
        className="p-button-sm p-button-outlined"
        disabled={rowData.setupStatus !== 'active'}
        onClick={() => handleEnterSchool(rowData)}
        tooltip={rowData.setupStatus !== 'active' ? 'School is not active' : 'Enter school'}
        tooltipOptions={{ position: 'left' }}
      />
      <Button
        icon="pi pi-credit-card"
        className="p-button-sm p-button-outlined p-button-secondary"
        disabled={!rowData.subscriptions || rowData.subscriptions.length === 0}
        onClick={() => handleBillingPortal(rowData)}
        tooltip="Billing portal"
        tooltipOptions={{ position: 'left' }}
      />
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-content-center align-items-center" style={{ height: "60vh" }}>
        <ProgressSpinner />
      </div>
    );
  }

  return (
    <div className="p-3">
      <div className="flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="m-0">Platform Dashboard</h2>
          <p className="text-500 mt-1 mb-0">Manage all registered schools</p>
        </div>
        <Button
          label="Register New School"
          icon="pi pi-plus"
          onClick={() => navigate("/platform/register-school")}
        />
      </div>

      <div className="grid mb-4">
        <div className="col-12 md:col-6 lg:col-3">
          <StatCard
            title="Total Schools"
            value={counts.total}
            icon="pi-building"
            color="#3b82f6"
          />
        </div>
        <div className="col-12 md:col-6 lg:col-3">
          <StatCard
            title="Active"
            value={counts.active}
            icon="pi-check-circle"
            color="#22c55e"
          />
        </div>
        <div className="col-12 md:col-6 lg:col-3">
          <StatCard
            title="Provisioning"
            value={counts.provisioning}
            icon="pi-spin pi-spinner"
            color="#f59e0b"
          />
        </div>
        <div className="col-12 md:col-6 lg:col-3">
          <StatCard
            title="Failed"
            value={counts.failed}
            icon="pi-times-circle"
            color="#ef4444"
          />
        </div>
      </div>

      <div className="card">
        <DataTable
          value={schools}
          paginator
          rows={10}
          emptyMessage="No schools registered yet."
          className="p-datatable-sm"
          sortField="createdAt"
          sortOrder={-1}
        >
          <Column field="schoolName" header="School Name" sortable />
          <Column field="adminEmail" header="Admin Email" sortable />
          <Column
            header="Plan"
            body={planTemplate}
            sortable
            sortField="plan.name"
          />
          <Column
            header="Subscription"
            body={subscriptionTemplate}
          />
          <Column
            field="setupStatus"
            header="Status"
            body={statusTemplate}
            sortable
          />
          <Column
            field="createdAt"
            header="Registered"
            body={dateTemplate}
            sortable
          />
          <Column
            header="Actions"
            body={actionTemplate}
            style={{ width: '10rem' }}
          />
        </DataTable>
      </div>

      <Toast ref={toast} />

      <Dialog
        header={
          <div className="flex align-items-center gap-2">
            <i className="pi pi-building" style={{ fontSize: '1.25rem', color: 'var(--primary-color)' }} />
            <span>Edit School Details</span>
          </div>
        }
        visible={editDialog}
        onHide={() => setEditDialog(false)}
        style={{ width: '580px' }}
        breakpoints={{ '640px': '95vw' }}
        className="p-fluid"
        footer={
          <div className="flex justify-content-end gap-2 pt-2">
            <Button label="Cancel" icon="pi pi-times" className="p-button-text p-button-secondary" onClick={() => setEditDialog(false)} />
            <Button label="Save Changes" icon="pi pi-check" loading={saving} onClick={handleEditSave} />
          </div>
        }
      >
        <div className="flex flex-column gap-4 pt-1">
          {/* School Information Section */}
          <div>
            <h4 className="mt-0 mb-3 text-600 font-medium text-sm uppercase letter-spacing-2">
              <i className="pi pi-building mr-2" />School Information
            </h4>
            <div className="grid">
              <div className="col-12">
                <label htmlFor="schoolName" className="block text-sm font-medium mb-1">School Name <span className="text-red-500">*</span></label>
                <InputText id="schoolName" value={editData.schoolName || ""} onChange={(e) => setEditData({ ...editData, schoolName: e.target.value })} className="w-full" />
              </div>
              <div className="col-12 md:col-6">
                <label htmlFor="email" className="block text-sm font-medium mb-1">School Email</label>
                <InputText id="email" value={editData.email || ""} onChange={(e) => setEditData({ ...editData, email: e.target.value })} className="w-full" />
              </div>
              <div className="col-12 md:col-6">
                <label htmlFor="phone" className="block text-sm font-medium mb-1">Phone</label>
                <InputText id="phone" value={editData.phone || ""} onChange={(e) => setEditData({ ...editData, phone: e.target.value })} className="w-full" />
              </div>
              <div className="col-12 md:col-6">
                <label htmlFor="address" className="block text-sm font-medium mb-1">Address</label>
                <InputText id="address" value={editData.address || ""} onChange={(e) => setEditData({ ...editData, address: e.target.value })} className="w-full" />
              </div>
              <div className="col-12 md:col-6">
                <label htmlFor="country" className="block text-sm font-medium mb-1">Country</label>
                <InputText id="country" value={editData.country || ""} onChange={(e) => setEditData({ ...editData, country: e.target.value })} className="w-full" />
              </div>
            </div>
          </div>

          {/* Admin Account Section */}
          <div>
            <h4 className="mt-0 mb-3 text-600 font-medium text-sm uppercase letter-spacing-2">
              <i className="pi pi-user mr-2" />Admin Account
            </h4>
            <div className="grid">
              <div className="col-12 md:col-6">
                <label htmlFor="adminName" className="block text-sm font-medium mb-1">Admin Name</label>
                <InputText id="adminName" value={editData.adminName || ""} onChange={(e) => setEditData({ ...editData, adminName: e.target.value })} className="w-full" />
              </div>
              <div className="col-12 md:col-6">
                <label htmlFor="adminEmail" className="block text-sm font-medium mb-1">Admin Email <span className="text-red-500">*</span></label>
                <InputText id="adminEmail" value={editData.adminEmail || ""} onChange={(e) => setEditData({ ...editData, adminEmail: e.target.value })} className="w-full" />
              </div>
              <div className="col-12">
                <label htmlFor="customDomain" className="block text-sm font-medium mb-1">Public Website Domain</label>
                <InputText id="customDomain" value={editData.customDomain || ""} onChange={(e) => setEditData({ ...editData, customDomain: e.target.value })} className="w-full" placeholder="e.g. greenfieldacademy.com" />
                <small className="text-color-secondary">The school's own domain. Its A record must point at this server; the public site is served at / and the portal stays at /login.</small>
              </div>
            </div>
          </div>

          {/* Password Reset Section */}
          <div className="border-top-1 surface-border pt-3">
            <div className="flex align-items-center gap-2 mb-2">
              <Checkbox inputId="resetPw" checked={resetPassword} onChange={(e) => { setResetPassword(e.checked); if (!e.checked) setEditData({ ...editData, newPassword: "" }); }} />
              <label htmlFor="resetPw" className="text-sm font-medium cursor-pointer">Reset admin password</label>
            </div>
            {resetPassword && (
              <div className="pl-4 mt-2 border-left-2 border-primary">
                <label htmlFor="newPassword" className="block text-sm font-medium mb-1">New Password <span className="text-red-500">*</span></label>
                <Password id="newPassword" value={editData.newPassword || ""} onChange={(e) => setEditData({ ...editData, newPassword: e.target.value })} toggleMask feedback={false} style={{ width: '100%' }} inputStyle={{ width: '100%' }} pt={{ iconField: { style: { width: '100%' } } }} placeholder="Min. 6 characters" />
              </div>
            )}
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default PlatformDashboard;
